import { CheckCircle, ArrowRight, Sparkles, Trophy, ShoppingCart, Target, Zap, Award, Newspaper, Cpu, BadgeCheck } from "lucide-react";

const AboutBrandBorn = () => {
  const features = [
    "#1 company in India for brand onboarding",
    "Official OMS Partner of Myntra",
    "India's 1st AI auto listings software — EcomCatalog.ai",
    "Fastest brand onboarding (launch in 14 days)",
    "Featured in Times of India & Economic Times",
    "Certified by BEEP, Google & Great Learning"
  ];

  const achievements = [
    {
      icon: Trophy,
      color: "from-amber-400 to-orange-500",
      shadow: "shadow-orange-200",
      title: "#1 Brand Onboarding Company in India",
      desc: "Ranked India's #1 e-commerce brand onboarding agency with 1000+ brands launched and a 98% first-time approval rate."
    },
    {
      icon: BadgeCheck,
      color: "from-pink-500 to-rose-500",
      shadow: "shadow-pink-200",
      title: "Official OMS Partner of Myntra",
      desc: "BrandBorn is a registered Official OMS (Onboarding & Marketplace Services) Partner of Myntra — direct access, faster approvals."
    },
    {
      icon: Cpu,
      color: "from-blue-500 to-indigo-500",
      shadow: "shadow-blue-200",
      title: "India's 1st AI Listings Software — EcomCatalog.ai",
      desc: "We built EcomCatalog.ai, India's first AI auto listings & data analysis software for e-commerce sellers. Marketplace-ready catalogs in 90 seconds."
    },
    {
      icon: Newspaper,
      color: "from-emerald-500 to-green-500",
      shadow: "shadow-green-200",
      title: "Featured in Times of India & Economic Times",
      desc: "Our e-commerce onboarding expertise has been featured in The Times of India and The Economic Times."
    }
  ];

  return (
    <section className="py-14 md:py-20 px-4 bg-gradient-to-b from-white via-slate-50 to-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-red-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-orange-500/5 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-red-500/3 to-orange-500/3 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-full text-sm font-bold mb-6 shadow-lg shadow-red-200/50">
              <Sparkles size={16} />
              WHO WE ARE
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-6 leading-tight">
              What is{" "}
              <span className="bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-transparent">
                BrandBorn?
              </span>
            </h2>
            
            <p className="text-lg text-gray-600 mb-4 leading-relaxed">
              <strong className="text-gray-900">BrandBorn</strong> is the{" "}
              <strong className="text-red-600">#1 company in India for brand onboarding</strong> and the{" "}
              <strong className="text-gray-900">Official OMS Partner of Myntra</strong>. We help D2C brands launch and grow on 
              premium online marketplaces with <strong className="text-gray-900">E-commerce Onboarding, Quick Commerce Onboarding, and Digital Commerce Onboarding</strong>.
            </p>
            
            <p className="text-base text-gray-500 mb-8 leading-relaxed">
              We built <a href="https://ecomcatalog.ai" target="_blank" rel="noopener noreferrer" className="font-bold text-blue-600 hover:underline">EcomCatalog.ai</a> — 
              <strong className="text-gray-800"> India's first AI auto listings & data analysis software</strong> for e-commerce sellers. 
              BrandBorn has been <strong className="text-gray-800">featured in Times of India &amp; <a href="https://economictimes.indiatimes.com/ai-innovator/profile/brandborn/133204259" target="_blank" rel="noopener noreferrer" className="text-red-600 underline">Economic Times (ET AI Innovator)</a></strong> and is{" "}
              <strong className="text-gray-800">certified by BEEP, Google & Great Learning</strong>. With{" "}
              <strong className="text-gray-800">1000+ brands launched</strong> at a{" "}
              <strong className="text-red-500">98% first-time approval rate</strong>, we onboard brands quickly — in just{" "}
              <strong className="text-gray-800">14 days</strong>, from Myntra to Blinkit.
            </p>

            {/* Features List */}
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3 group">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform">
                    <CheckCircle className="text-white" size={12} />
                  </div>
                  <span className="text-gray-700 font-medium text-sm">{feature}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="group flex items-center gap-2 px-10 py-5 bg-red-600 text-white rounded-2xl font-black text-base tracking-wider uppercase shadow-[0_4px_20px_rgba(220,38,38,0.3)] hover:bg-red-700 hover:shadow-[0_12px_40px_rgba(220,38,38,0.4)] hover:-translate-y-1 transition-all duration-500"
            >
              Check Eligibility
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Right Side - 3D Floating Stats */}
          <div className="relative" style={{ perspective: '1000px' }}>
            {/* Background glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-3xl blur-2xl scale-105" />
            
            {/* Main container with 3D tilt */}
            <div className="relative" style={{ transformStyle: 'preserve-3d' }}>
              {/* Top Row Cards */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Card 1 - Brands */}
                <div className="group bg-white rounded-2xl p-6 shadow-xl border border-gray-100/80 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2" style={{ transform: 'rotateY(-5deg) rotateX(3deg)', transformStyle: 'preserve-3d' }}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-4 shadow-lg shadow-orange-200 group-hover:scale-110 transition-transform" style={{ transform: 'translateZ(20px)' }}>
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-4xl font-black bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-transparent mb-1" style={{ transform: 'translateZ(15px)' }}>1000+</div>
                  <div className="text-sm text-gray-500 font-medium">Brands Onboarded</div>
                </div>

                {/* Card 2 - Platforms */}
                <div className="group bg-white rounded-2xl p-6 shadow-xl border border-gray-100/80 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2" style={{ transform: 'rotateY(5deg) rotateX(3deg)', transformStyle: 'preserve-3d' }}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center mb-4 shadow-lg shadow-blue-200 group-hover:scale-110 transition-transform" style={{ transform: 'translateZ(20px)' }}>
                    <ShoppingCart className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-4xl font-black bg-gradient-to-r from-blue-600 to-indigo-500 bg-clip-text text-transparent mb-1" style={{ transform: 'translateZ(15px)' }}>70+</div>
                  <div className="text-sm text-gray-500 font-medium">Partner Platforms</div>
                </div>
              </div>

              {/* Bottom Row Cards */}
              <div className="grid grid-cols-2 gap-4">
                {/* Card 3 - Success Rate */}
                <div className="group bg-white rounded-2xl p-6 shadow-xl border border-gray-100/80 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2" style={{ transform: 'rotateY(-5deg) rotateX(-3deg)', transformStyle: 'preserve-3d' }}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center mb-4 shadow-lg shadow-green-200 group-hover:scale-110 transition-transform" style={{ transform: 'translateZ(20px)' }}>
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-4xl font-black bg-gradient-to-r from-emerald-600 to-green-500 bg-clip-text text-transparent mb-1" style={{ transform: 'translateZ(15px)' }}>98%</div>
                  <div className="text-sm text-gray-500 font-medium">Success Rate</div>
                </div>

                {/* Card 4 - Speed */}
                <div className="group bg-white rounded-2xl p-6 shadow-xl border border-gray-100/80 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2" style={{ transform: 'rotateY(5deg) rotateX(-3deg)', transformStyle: 'preserve-3d' }}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center mb-4 shadow-lg shadow-purple-200 group-hover:scale-110 transition-transform" style={{ transform: 'translateZ(20px)' }}>
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-4xl font-black bg-gradient-to-r from-violet-600 to-purple-500 bg-clip-text text-transparent mb-1" style={{ transform: 'translateZ(15px)' }}>14</div>
                  <div className="text-sm text-gray-500 font-medium">Days to Launch</div>
                </div>
              </div>

              {/* Floating Trust Badge */}
              <div className="mt-6 bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-gray-100 shadow-lg">
                <div className="flex items-center justify-center gap-6 flex-wrap">
                  <span className="text-xs text-gray-400 font-medium">Trusted by brands on</span>
                  {["Myntra", "Nykaa", "Ajio", "Blinkit"].map((p, i) => (
                    <span key={i} className={`px-3 py-1.5 rounded-full text-xs font-bold shadow-sm ${
                      i === 0 ? 'bg-pink-50 text-pink-600 border border-pink-100' :
                      i === 1 ? 'bg-rose-50 text-rose-600 border border-rose-100' :
                      i === 2 ? 'bg-slate-50 text-slate-700 border border-slate-200' :
                      'bg-amber-50 text-amber-700 border border-amber-100'
                    }`}>{p}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating #1 Badge */}
            <div className="absolute -top-3 -right-3 z-20">
              <div className="relative">
                <div className="absolute inset-0 bg-green-400 rounded-full blur-md opacity-50 animate-pulse" />
                <div className="relative bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full font-black text-sm shadow-xl">
                  #1 in India
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Achievements - SEO rich cards */}
        <div className="mt-14 md:mt-20" data-testid="about-achievements">
          <h3 className="text-2xl md:text-3xl font-black text-gray-900 text-center mb-3">
            Why BrandBorn is India's{" "}
            <span className="bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-transparent">#1 Brand Onboarding Company</span>
          </h3>
          <p className="text-center text-gray-500 text-sm md:text-base mb-10 max-w-3xl mx-auto">
            Official Myntra OMS Partner • India's 1st AI Listings Software (EcomCatalog.ai) • Featured in Times of India & Economic Times • Certified by BEEP, Google & Great Learning
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {achievements.map((item, idx) => (
              <div
                key={idx}
                data-testid={`achievement-card-${idx}`}
                className="group bg-white rounded-2xl p-6 shadow-lg border border-gray-100/80 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4 shadow-lg ${item.shadow} group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-base font-black text-gray-900 mb-2 leading-snug">{item.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Certifications strip */}
          <div className="mt-8 bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-gray-100 shadow-lg" data-testid="about-certifications-strip">
            <div className="flex items-center justify-center gap-4 md:gap-8 flex-wrap">
              <span className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <Award className="w-5 h-5 text-red-500" /> Certified By:
              </span>
              {["BEEP Certified", "Google Certified", "Great Learning Certified"].map((c, i) => (
                <span key={i} className={`px-4 py-2 rounded-full text-xs font-bold shadow-sm border ${
                  i === 0 ? 'bg-blue-50 text-blue-700 border-blue-100' :
                  i === 1 ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                  'bg-orange-50 text-orange-700 border-orange-100'
                }`}>
                  <CheckCircle className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />{c}
                </span>
              ))}
              <span className="hidden md:inline-block w-px h-6 bg-gray-200" />
              <span className="flex items-center gap-3 text-xs font-bold text-gray-500">
                <Newspaper className="w-4 h-4 text-gray-400" /> Featured In:
                <img src="/images/logos/times-of-india.png" alt="The Times of India" className="h-8 object-contain" loading="lazy" />
                <img src="/images/logos/economic-times.png" alt="The Economic Times" className="h-5 object-contain" loading="lazy" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutBrandBorn;
